import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteConversation,
  fetchConversations,
  fetchMessages,
  markConversationRead,
  sendHumanText,
  setConversationStatus,
  windowOpen,
  type WaConversation,
  type WaMessage,
} from "@/services/waAgent";
import { timeAgo } from "@/lib/timeAgo";
import { btnGhost, btnPrimary, cardCls, inputCls } from "./ui";

type Filter = "all" | "human" | "bot" | "closed";

const STATUS_PILL: Record<WaConversation["status"], { label: string; cls: string }> = {
  bot: { label: "Agente IA", cls: "bg-primary-500/15 text-primary-700" },
  human: { label: "Atiende una persona", cls: "bg-amber-500/15 text-amber-700" },
  closed: { label: "Cerrada", cls: "bg-background-200 text-foreground-600" },
};

const REASON: Record<string, string> = {
  max_auto_replies: "Llegó al límite de respuestas automáticas",
  media: "Envió un audio, imagen u otro archivo",
  sin_respuesta: "El agente no sabía responder",
  compliance: "La respuesta no pasó el filtro de cumplimiento",
  opt_out: "Pidió no recibir más mensajes",
  ia_error: "Error del motor de IA",
  send_failed: "No se pudo enviar la respuesta",
  ollama_not_configured: "Falta la clave del motor de IA",
};
const reasonText = (r: string | null) => (r ? (r.startsWith("keyword:") ? `Escribió “${r.slice(8)}”` : REASON[r] ?? r) : null);

const TICK: Record<string, string> = { sent: "ri-check-line", delivered: "ri-check-double-line", read: "ri-check-double-line text-sky-500", failed: "ri-error-warning-line text-accent-600" };

export default function InboxTab({ notify }: { notify: (m: string) => void }) {
  const [convs, setConvs] = useState<WaConversation[]>([]);
  const [msgs, setMsgs] = useState<WaMessage[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const end = useRef<HTMLDivElement>(null);
  const selRef = useRef<string | null>(null);
  selRef.current = sel;

  const loadList = useCallback(async () => {
    try {
      setConvs(await fetchConversations());
    } catch {
      /* se reintenta en el siguiente ciclo */
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMsgs = useCallback(async (id: string) => {
    try {
      const m = await fetchMessages(id);
      if (selRef.current === id) setMsgs(m);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void loadList();
    const t = window.setInterval(() => {
      void loadList();
      if (selRef.current) void loadMsgs(selRef.current);
    }, 8000);
    return () => window.clearInterval(t);
  }, [loadList, loadMsgs]);

  useEffect(() => {
    if (!sel) return;
    setMsgs([]);
    void loadMsgs(sel);
    void markConversationRead(sel).then(() => loadList());
  }, [sel, loadMsgs, loadList]);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [msgs.length, sel]);

  const cur = convs.find((c) => c.id === sel) ?? null;
  const shown = convs.filter((c) => filter === "all" || c.status === filter);
  const attention = convs.filter((c) => c.status === "human" && c.unread > 0).length;
  const open = cur ? windowOpen(cur.lastInboundAt) : false;

  const setStatus = async (s: "bot" | "human" | "closed") => {
    if (!cur) return;
    try {
      await setConversationStatus(cur.id, s);
      await loadList();
      notify(s === "bot" ? "El agente vuelve a atender esta conversación" : s === "human" ? "Ahora la atiendes tú" : "Conversación cerrada");
    } catch {
      notify("No se pudo cambiar el estado");
    }
  };

  const send = async () => {
    if (!cur || !text.trim() || busy) return;
    setBusy(true);
    const r = await sendHumanText(cur.id, text.trim());
    setBusy(false);
    if (r.ok) {
      setText("");
      await Promise.all([loadMsgs(cur.id), loadList()]);
    } else notify(r.error === "window_closed" ? "Pasaron más de 24 h: solo puedes escribir con una plantilla aprobada" : r.error ?? "No se pudo enviar");
  };

  return (
    <div className="grid md:grid-cols-[320px_minmax(0,1fr)] gap-4 items-start">
      <div className={`${cardCls} !p-3 ${sel ? "hidden md:block" : ""}`}>
        <div className="flex gap-1 overflow-x-auto mb-3">
          {([["all", "Todas"], ["human", attention ? `Personas (${attention})` : "Personas"], ["bot", "Agente"], ["closed", "Cerradas"]] as [Filter, string][]).map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer ${filter === k ? "bg-primary-500 text-background-950" : "bg-background-200 text-foreground-700"}`}>
              {l}
            </button>
          ))}
        </div>
        {loading ? <div className="text-sm text-foreground-500 p-3">Cargando…</div> : null}
        {!loading && !shown.length ? <div className="text-sm text-foreground-500 p-3">No hay conversaciones todavía. Aparecerán aquí cuando alguien escriba a tu número de WhatsApp.</div> : null}
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-background-200">
          {shown.map((c) => (
            <button key={c.id} onClick={() => setSel(c.id)} className={`w-full text-left px-3 py-3 cursor-pointer rounded-lg ${sel === c.id ? "bg-background-200" : "hover:bg-background-50"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm text-foreground-950 truncate">{c.name || `+${c.phone}`}</span>
                <span className="text-[10px] text-foreground-500 whitespace-nowrap">{timeAgo(c.lastMessageAt)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-xs text-foreground-600 truncate">{c.preview ?? ""}</span>
                {c.unread > 0 ? <span className="bg-primary-500 text-background-950 text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">{c.unread}</span> : null}
              </div>
              <span className={`inline-block mt-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[c.status].cls}`}>{STATUS_PILL[c.status].label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={`${cardCls} !p-0 flex flex-col min-h-[60vh] ${sel ? "" : "hidden md:flex"}`}>
        {!cur ? (
          <div className="m-auto text-sm text-foreground-500 p-8 text-center">Elige una conversación para verla.</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-background-200 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <button className="md:hidden text-foreground-600 cursor-pointer" onClick={() => setSel(null)} aria-label="Volver">
                  <i className="ri-arrow-left-line text-lg"></i>
                </button>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-foreground-950 truncate">{cur.name || `+${cur.phone}`}</div>
                  <div className="text-[11px] text-foreground-500">
                    +{cur.phone} · {open ? "Ventana de 24 h abierta" : "Ventana cerrada (solo plantillas)"}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {cur.status !== "bot" && cur.status !== "closed" ? (
                  <button className={btnGhost} onClick={() => void setStatus("bot")}>
                    Devolver al agente
                  </button>
                ) : null}
                {cur.status === "bot" ? (
                  <button className={btnGhost} onClick={() => void setStatus("human")}>
                    Tomar yo
                  </button>
                ) : null}
                <button
                  className="w-9 h-9 rounded-full text-foreground-500 hover:bg-accent-500/10 hover:text-accent-700 cursor-pointer"
                  aria-label="Eliminar conversación"
                  title="Eliminar conversación"
                  onClick={async () => {
                    if (!window.confirm("¿Eliminar esta conversación y todos sus mensajes? No se puede deshacer.")) return;
                    try {
                      await deleteConversation(cur.id);
                      setSel(null);
                      await loadList();
                      notify("Conversación eliminada");
                    } catch {
                      notify("No se pudo eliminar");
                    }
                  }}
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
                {cur.status !== "closed" ? (
                  <button className={btnGhost} onClick={() => void setStatus("closed")}>
                    Cerrar
                  </button>
                ) : (
                  <button className={btnGhost} onClick={() => void setStatus("human")}>
                    Reabrir
                  </button>
                )}
              </div>
            </div>
            {cur.status === "human" && cur.handoffReason ? (
              <div className="px-4 py-2 text-xs bg-amber-500/10 text-amber-800">
                <i className="ri-user-voice-line mr-1"></i>Pasó a una persona: {reasonText(cur.handoffReason)}
              </div>
            ) : null}

            <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[55vh] bg-background-50">
              {msgs.map((m) => {
                const mine = m.direction === "out";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${mine ? (m.sender === "bot" ? "bg-primary-500/20 text-foreground-950" : "bg-primary-500 text-background-950") : "bg-background-100 border border-background-200 text-foreground-900"}`}>
                      {m.body}
                      <div className="text-[10px] opacity-70 mt-1 flex items-center gap-1.5 justify-end">
                        {mine ? <span>{m.sender === "bot" ? "Agente IA" : m.sender === "template" ? `Plantilla${m.templateName ? ` · ${m.templateName}` : ""}` : "Tú"}</span> : null}
                        <span>{new Date(m.createdAt).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}</span>
                        {mine && m.status ? <i className={TICK[m.status] ?? ""} title={m.error ?? m.status}></i> : null}
                      </div>
                      {m.status === "failed" && m.error ? <div className="text-[10px] text-accent-700 mt-0.5">{m.error}</div> : null}
                    </div>
                  </div>
                );
              })}
              <div ref={end}></div>
            </div>

            <div className="p-3 border-t border-background-200">
              {cur.status === "closed" ? (
                <div className="text-xs text-foreground-500 text-center py-1">Conversación cerrada. Pulsa “Reabrir” para escribir.</div>
              ) : !open ? (
                <div className="text-xs text-foreground-600 text-center py-1">
                  Pasaron más de 24 h desde su último mensaje: Meta solo permite escribirle con una plantilla aprobada (pestaña Plantillas → enviar prueba).
                </div>
              ) : (
                <div className="flex gap-2">
                  <textarea
                    rows={1}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void send();
                      }
                    }}
                    placeholder={cur.status === "bot" ? "Si escribes, tomas la conversación y el agente se detiene…" : "Escribe tu respuesta…"}
                    className={`${inputCls} resize-none`}
                  />
                  <button className={btnPrimary} disabled={busy || !text.trim()} onClick={() => void send()}>
                    Enviar
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
